// App-side delegate for `@heidi/templates/data` (D2.3, ARCFE-1029).
//
// The package hook owns the request and the three template-list invalidations.
// EVERYTHING below the invalidations stayed here, in its original order,
// because every statement of it was an app reaction:
//
//   1. the posthog flag read + `invalidateCurrentUser()`  (workspace cache)
//   2. `invalidateGamifyTrialExtension()`                 (commerce cache)
//   3. three analytics dispatches, one of them behind `isOrganizationTemplateManager`
//   4. the success toast, whose action button writes a template-builder atom
//
// 1 and 2 are the reason this hook could never have taken its invalidations
// wholesale into the package: they reach into two OTHER domains' caches, and a
// templates package invalidating a workspace key is exactly the cross-domain
// edge the carve exists to prevent.
import { editTemplateModalOpenAtom } from '@/atoms/templateBuilderAtoms';
import { IS_TEMPLATE_CREATION_RECORD_ENABLED } from '@/features/flags/legacy-flags';
import { countMarkdownSections } from '@/helpers/sectionParser';
import { useCombinedOrganizationInfo } from '@/hooks/query/useCombinedOrganizationInfo';
import { trackCommunityTemplatePublished } from '@/lib/analytics/scribe/community';
import { trackGlobalTemplateCreated } from '@/lib/analytics/scribe/global-template';
import { trackTemplateCreated } from '@/lib/analytics/scribe/templates';
import { type TemplateCreationSource } from '@/models/analytics/features/templates';
import { Button } from '@heidi/design-system/button';
import { toast } from '@heidi/design-system/toast';
import { useTranslations } from '@heidi/runtime/i18n';
import { TemplateVisibility } from '@heidi/schema/ml-scribe';
import {
  isRegulatoryGuidelineWarningModalV2OpenAtom,
  regulatoryGuidelineBlockModalOpenAtom,
  regulatoryGuidelineComplianceReasonAtom,
  regulatoryGuidelineWarningModalOpenAtom,
} from '@heidi/templates';
import { useMutateCreateTemplate as useMutateCreateTemplateCore } from '@heidi/templates/data';
import { useSetAtom } from 'jotai';
import posthog from 'posthog-js';
import React from 'react';
import { useIntl } from 'react-intl';
import { useQueryConsultNoteTemplates } from '../query/useQueryConsultNoteTemplates';
import { invalidateGamifyTrialExtension } from '../query/useQueryGamifyTrialExtension';
import { invalidateCurrentUser } from '../query/useQueryUser';
import { handleTemplateComplianceError } from './handleTemplateComplianceError';

// `export type { … }`, not `export { type … }` — the inline modifier reads as a
// VALUE export to tooling that inspects the declaration (it cost +4 on the
// react-refresh ratchet from one such re-export in `templateBuilderAtoms.tsx`).
export type { CreateTemplateProps } from '@heidi/templates/contracts';

/** The analytics-only fields this app carries on the mutation variables. */
type TemplateCreationAnalytics = {
  /** Analytics-only: how this template creation was initiated. */
  creationSource?: TemplateCreationSource;
  /** Analytics-only: the prompt behind an `ask_ai` creation. */
  creationAiCommandText?: string;
};

export function useMutateCreateTemplate({
  showToast = true,
}: { showToast?: boolean } = {}) {
  const { t } = useTranslations();
  const intl = useIntl();
  const { isOrganizationTemplateManager } = useCombinedOrganizationInfo();
  // to trigger api call
  useQueryConsultNoteTemplates();

  const setIsRegulatoryGuidelineBlockModalOpen = useSetAtom(
    regulatoryGuidelineBlockModalOpenAtom
  );
  const setIsRegulatoryGuidelineWarningModalOpen = useSetAtom(
    regulatoryGuidelineWarningModalOpenAtom
  );

  const setIsRegulatoryGuidelineWarningModalV2Open = useSetAtom(
    isRegulatoryGuidelineWarningModalV2OpenAtom
  );
  const setRegulatoryGuidelineComplianceReason = useSetAtom(
    regulatoryGuidelineComplianceReasonAtom
  );

  const setEditTemplateDialog = useSetAtom(editTemplateModalOpenAtom);

  return useMutateCreateTemplateCore<TemplateCreationAnalytics>({
    onCreated: (template, variables) => {
      if (posthog.getFeatureFlag('template-library-tag') !== 'control' || true) {
        // to make the template banner and tag dissapear
        void invalidateCurrentUser();
      }

      void invalidateGamifyTrialExtension();

      if (template.template_visibility === TemplateVisibility.Community) {
        trackCommunityTemplatePublished({
          templateId: template.id,
        });
      }

      const handleEditTemplate = () => {
        setEditTemplateDialog(template);
      };

      if (isOrganizationTemplateManager) {
        trackGlobalTemplateCreated({
          templateId: template.id,
          visibility: template.template_visibility,
          sharedTeams: template.shared_team_ids || [],
        });
      }

      trackTemplateCreated({
        templateId: template.id,
        templateName: template.name,
        teamId: template.team_id ?? undefined,
        version: IS_TEMPLATE_CREATION_RECORD_ENABLED() ? 'v2' : 'v1',
        sectionCount: countMarkdownSections(variables.input.structure_template),
        source: variables.creationSource,
        aiCommandText: variables.creationAiCommandText,
        useSections: variables.input.use_sections ?? undefined,
      });

      if (showToast) {
        toast({
          tone: 'success',
          title: t('templateEditor.templateSaved', { name: template.name }),
          action: React.createElement(
            Button,
            {
              size: 'xs',
              variant: 'ghost',
              onClick: () => void handleEditTemplate(),
            },
            t('templateEditor.view')
          ),
        });
      }
    },
    onCreateFailed: (error, variables) => {
      handleTemplateComplianceError({
        error,
        templateContent:
          variables.input.structure_template || variables.input.template_html || '',
        templateSource: 'create',
        formatMessage: intl.formatMessage,
        toast,
        setIsRegulatoryGuidelineBlockModalOpen,
        setIsRegulatoryGuidelineWarningModalOpen,
        setIsRegulatoryGuidelineWarningModalV2Open,
        setRegulatoryGuidelineComplianceReason,
      });
    },
  });
}
